import { z } from "zod";

// Wire shapes for Send to Optro, snake_case throughout per the backend (no
// camelCase alias).

// Why an artifact did not land. A send always overwrites (Optro keeps the
// version history), so nothing here means "edited since the last send".
export const ArtifactReasonSchema = z.enum([
  "checked_out",
  "attachable_gone",
  "unsupported_storage",
  "too_large",
  "generation_failed",
  "permission_denied",
  "reauth_required",
  "unreachable",
]);

export type ArtifactReason = z.infer<typeof ArtifactReasonSchema>;

// `status` must parse: it says whether the file landed. A reason this build
// does not know (one a newer backend added) reads as no reason, so a finished
// send still reports instead of failing every poll. The rest only fill in the
// copy, so a payload that omits one still reports the outcome.
export const ArtifactOutcomeSchema = z.object({
  status: z.enum(["created", "versioned", "failed"]),
  reason: ArtifactReasonSchema.nullable().catch(null),
  // Written by the backend for a user, so it may be shown verbatim.
  user_message: z.string().nullish(),
  // checked_out: who holds the file in Optro, and since when (ISO datetime).
  checked_out_by: z.string().nullish(),
  checked_out_at: z.string().nullish(),
  // too_large: the file's size and Optro's limit.
  size_bytes: z.number().nullish(),
  max_size_bytes: z.number().nullish(),
});

export type ArtifactOutcome = z.infer<typeof ArtifactOutcomeSchema>;

export const SendOutcomeSchema = z.object({
  workpaper: ArtifactOutcomeSchema,
  evidence: ArtifactOutcomeSchema,
});

export type SendOutcome = z.infer<typeof SendOutcomeSchema>;

export const isArtifactLanded = (artifact: ArtifactOutcome): boolean =>
  artifact.status === "created" || artifact.status === "versioned";

export const StartOptroSendResponseSchema = z.object({
  task_id: z.string(),
});

// `error` is user copy whenever it is set.
export const OptroSendJobStatusSchema = z.object({
  task_id: z.string(),
  status: z.enum(["pending", "running", "success", "failure"]),
  outcome: SendOutcomeSchema.nullable(),
  error: z.string().nullable(),
});

export type OptroSendJobStatus = z.infer<typeof OptroSendJobStatusSchema>;

// The start endpoint refuses with `detail: { code, message }`; the copy is
// picked from `code`, filled in for workpaper_checked_out by who holds it. A
// 403 from the Midship permission check or another router-level refusal
// carries a plain string instead.
export const SendRefusalSchema = z.object({
  code: z.enum([
    "send_in_progress",
    "send_disabled",
    "optro_sign_in_required",
    "not_linked",
    "session_not_found",
    "workpaper_checked_out",
    "optro_upload_not_allowed",
  ]),
  checked_out_by: z.string().nullish(),
  checked_out_at: z.string().nullish(),
});

export type SendRefusalCode = z.infer<typeof SendRefusalSchema>["code"];
